import { useMemo } from 'react';

import { DownloadModal } from './DownloadModal';
import { isBundleVariant, type DownloadDefinition } from './types';
import type { DownloadController } from './useDownloadController';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';

interface Props {
  definition: DownloadDefinition;
  controller: DownloadController;
}

interface ProgressConfig {
  progressEndpoint: string;
  statusEndpoint?: string;
  itemName: string;
  readyDescription?: string;
}

export const DownloadFlow = ({ definition, controller }: Props) => {
  const activeAsync = controller.asyncExport;
  const activeVariant = useMemo(
    () => (activeAsync ? definition.variants.find((v) => v.id === activeAsync.variantId) : undefined),
    [activeAsync, definition.variants],
  );

  const progress = useMemo<ProgressConfig | null>(() => {
    if (!activeAsync || !activeVariant) return null;
    const { exportId } = activeAsync;

    if (isBundleVariant(activeVariant)) {
      return {
        progressEndpoint: activeVariant.progressEndpoint(exportId),
        statusEndpoint: activeVariant.statusEndpoint?.(exportId),
        itemName: activeVariant.label,
        readyDescription: activeVariant.readyDescription,
      };
    }

    if ('mode' in activeVariant && activeVariant.mode === 'async') {
      return {
        progressEndpoint: activeVariant.progressEndpoint(exportId),
        statusEndpoint: activeVariant.statusEndpoint?.(exportId),
        itemName: activeVariant.label,
        readyDescription: activeVariant.readyDescription,
      };
    }

    return null;
  }, [activeAsync, activeVariant]);

  return (
    <>
      <DownloadModal
        open={controller.isOpen}
        onOpenChange={(o) => (o ? controller.open() : controller.close())}
        definition={definition}
        onAsyncStarted={controller.startAsync}
      />

      {activeAsync && progress && (
        <GenericExportProgressModal
          isOpen
          onClose={controller.dismissProgress}
          exportConfig={{
            progressEndpoint: progress.progressEndpoint,
            statusEndpoint: progress.statusEndpoint,
            exportId: activeAsync.exportId,
          }}
          itemName={progress.itemName}
          isCreating
          readyDescription={progress.readyDescription}
        />
      )}
    </>
  );
};
